'use client';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  // useQueryClient
} from '@tanstack/react-query';
import type {
  IUserAccount,
  PropertyField,
  CreateAccountRequest,
  UpdateAccountRequest,
  User,
  CreateAndSwitchAccountResponse,
} from '@/modules/authentication/types';
import { usePagination } from './hooks/usePagination';
import {
  ICrop,
  ITimeZone,
  CreateAccountPropertyRequest,
  AccountPropertyResponse,
  SwitchPropertyRequest,
  ICreatePropertyBody,
  ITeamTypeResponse,
  ITeamOnboardingBody,
  ITeamOnboardingSchema,
  AddPropertyResponse,
  NameIdPair,
  PendingInvitationsRes,
} from './types';
import { apiFetch } from './apiFetch';
import { IPLocation } from '../onboarding/utils';
import { IFieldDetails } from '../field/types/api';
// import { messageBus } from './messages';
// import { saveUserSession } from '../authentication/utils';
// import { useBoundStore } from './stores/store';
// import { useShallow } from 'zustand/react/shallow';
import { useMainAppStore } from './stores/mainAppStore';
import { clearPlotsCache, updatePlotsInCache } from '../field/utils/plotsCache';
import { clearOpenThread } from '../orth/sessionKeys';
import { isEnterprisePlan } from './plan';

// GENERAL APP ENDPOINTS

export function useGetCrops({
  search,
  enabled,
}: { search?: string; enabled?: boolean } = {}) {
  return usePagination<ICrop>({
    queryKey: ['CROPS', search],
    endpoint: "crops",
    search,
    resource: "crops",
    enabled
  });
}

export function useUserProfile(enabled?: boolean) {
  return useQuery({
    staleTime: Infinity,
    queryKey: ['userProfile'],
    queryFn: async () =>
      apiFetch<User>('/api/v1/users/profile', { method: 'GET' }),
    enabled: enabled ?? true,
    select: (data) => {
      const planName = data.lastActiveAccount.subscription?.name;
      const isEnterprise = isEnterprisePlan(planName);
      return {
        ...data,
        isSubscribed: planName !== 'free' && planName !== 'reverse_trial',
        // Enterprise is a contract tier that ships the full Teams feature set,
        // so it answers true here and every Teams flow lights up for it. What it
        // must NOT get is the payment UI — gate that on `isEnterprise`, never on
        // this flag (see `shared/plan.ts`).
        isTeamsSubscription: planName?.includes('team') || isEnterprise,
        isReverseTrial: planName === 'reverse_trial',
        isEnterprise,
      };
    },
  });
}

export function useGetAccountProperties(accountId?: number, enabled?: boolean) {
  return usePagination<PropertyField>({
    queryKey: ['accountProperties', accountId],
    endpoint: `account-properties`,
    resource: 'accountProperties',
    params: { accountId },
    enabled,
  });
}

export function useGetAccountPropertiesLookup() {
  return useQuery({
    queryKey: ['accountPropertiesLookup'],
    queryFn: async () => {
      return await apiFetch<{ properties: NameIdPair[] }>(
        `/api/v1/account-properties/lookup`
      );
    },
    select: (data) => {
      return data.properties;
    },
  });
}
// ************************ TEAM TYPE ************************ //
export function useGetTeamType() {
  return useInfiniteQuery({
    queryKey: ['getTeamType'],
    queryFn: async ({ pageParam = 1 }) => {
      return await apiFetch<ITeamTypeResponse>('/api/v1/team-types', {
        method: 'GET',
        params: {
          page: pageParam,
          limit: 10,
        },
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = allPages.length;
      const totalPages = Math.ceil(lastPage.total / 10);
      if (currentPage >= totalPages) {
        return undefined;
      }
      return currentPage + 1;
    },
    select: (data) => data.pages.flatMap((page) => page.teamTypes),
  });
}
export function useGetTeamOnboardingInfo() {
  return useQuery({
    queryKey: ['teamOnboardingInfo'],
    queryFn: async () => {
      return await apiFetch<ITeamOnboardingSchema>(
        `/api/v1/subscription/stripe/team-onboarding`,
        {
          method: 'GET',
        }
      );
    },
  });
}
export function useFinishTeamOnboarding() {
  return useMutation({
    mutationFn: async (
      body: Omit<ITeamOnboardingBody, 'jobTitleId' | 'emails'> & {
        jobTitleId?: number;
        invitedEmails?: string[]
      }
    ) => {
      return await apiFetch(`/api/v1/subscription/stripe/team-onboarding`, {
        method: 'POST',
        body,
      });
    },
  });
}
// ************************ TIME ZONES ************************ //
export function useGetTimeZones() {
  return useQuery({
    queryKey: ['TIMEZONES'],
    queryFn: async () => {
      return await apiFetch<ITimeZone[]>(`/api/v1/country-timezone/timezones`, {
        method: 'GET',
      });

    },
  });
}

// Account Properties (Farms)
export function useCreateAccountProperty() {
  return useMutation({
    mutationFn: async (body: CreateAccountPropertyRequest) => {
      return await apiFetch<AccountPropertyResponse>(`/api/v1/account-properties`, {
        method: 'POST',
        body,
      });
    }
  });
}

export type SwitchAccountResponse = {
  user: User;
  accessToken: string;
  refreshToken: string;
};

export async function switchAccountApi(body: SwitchPropertyRequest) {
  return apiFetch<SwitchAccountResponse>(`/api/v1/users/switch-account`, {
    method: 'PATCH',
    body,
  });
}

export function useSwitchProperty() {
  //TODO: uncomment this when we have a way to switch account in  the same session without any problem
  // const { setAccessToken, setPlotId } = useBoundStore(
  //   useShallow((s) => ({
  //     setAccessToken: s.setAccessToken,
  //     setPlotId: s.setPlotId,
  //   }))
  // );
  const setAppLoading = useMainAppStore((s) => s.setAppLoading);
  // const queryClient = useQueryClient();
  // const { replace } = useRouter();

  return useMutation({
    mutationFn: async (body: SwitchPropertyRequest) => {
      setAppLoading(true);
      return switchAccountApi(body);
    },
    onSuccess: async () => {
      clearPlotsCache();
      // Threads are scoped to a property, so the open one does not survive the
      // switch — otherwise the reload every caller does next would restore a
      // thread from the farm the user just left. A chat deep link is unaffected:
      // it names its thread in the URL, not in this key.
      clearOpenThread();
      //TODO: uncomment this when we have a way to switch account in  the same session without any problem
      //setAccessToken(res.accessToken);
      //await saveUserSession(res);
      /*setPlotId(undefined);
      messageBus.publish('orth:reset');
      messageBus.publish("graphics:clear")
      const querykeys = new Set([
        'userProfile',
        "getNotificationCenter",
      ]);
      await queryClient.invalidateQueries({
        predicate: (query) =>
          querykeys.has(String(query.queryKey[0])),
      });
      replace(routes.orth);*/

    },
    onSettled: () => setAppLoading(false),
  });
}

export function useGetUsersAccounts() { // spaces
  return usePagination<IUserAccount>({
    queryKey: ["SPACES"],
    endpoint: "users/accounts",
    resource: "accounts",
  });
}

export const useCreateAccount = () => { // Create and Switch 
  return useMutation({
    mutationFn: async (body: CreateAccountRequest) => {
      return await apiFetch<Omit<CreateAndSwitchAccountResponse, 'subscription'>>(`/api/v1/accounts/create-and-switch`, {
        method: 'POST',
        body,
      });
    },
  });
};

export const useUpdateAccount = () => {
  return useMutation({
    mutationFn: async (args: { id: number; body: UpdateAccountRequest }) => {
      const { id, body } = args;
      return await apiFetch(`/api/v1/accounts/${id}`, {
        method: 'PUT',
        body,
      });
    },
  });
};

export const useGetPropertyFieldsLookup = (params?: { cropIds?: number[] }) => {
  return useQuery({
    queryKey: ['getAllPropertyFields', params],
    queryFn: async () => {
      return await apiFetch<{ fields: NameIdPair[] }>(
        `/api/v1/property-fields/lookup`,
        {
          params,
          paramsSerializer: {
            indexes: null,
          },
        }
      );
    },
    select: (data) => data.fields,
  });
};

export const useGetAllPropertyFields = (
  params?: {
    search?: string;
    cropId?: number;
    varietyId?: number;
  },
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: ['getAllPropertyFields', params],
    queryFn: async () => {
      return await apiFetch<{ fields: NameIdPair[] }>(
        `/api/v1/property-fields/lookup`,
        {
          params,
          paramsSerializer: {
            indexes: null,
          },
        }
      );
    },
    enabled: options?.enabled,
  });
};

export const useAddProperty = () => {
  return useMutation({
    mutationFn: async (args: { accountId: number; body: ICreatePropertyBody }) => {
      const { accountId, body } = args;
      return await apiFetch<AddPropertyResponse>(`/api/v1/account-properties?accountId=${accountId}`, {
        method: 'POST',
        body,

      });
    },
  });
}

export const useEditProperty = (onSuccess?: () => void) => {
  return useMutation({
    mutationFn: async (args: {
      accountId: number;
      propertyId: number;
      body: Partial<ICreatePropertyBody>;
    }) => {
      const { accountId, body, propertyId } = args;
      return await apiFetch(
        `/api/v1/account-properties/${propertyId}?accountId=${accountId}`,
        {
          method: 'PUT',
          body,
        }
      );
    },
    onSuccess: () => {
      onSuccess?.();
    },
  });
};


export const useDeletePropertyThreads = () => {
  return useMutation({
    mutationFn: async (args: { accountId: number; propertyId: number }) => {
      const { accountId, propertyId } = args;
      return await apiFetch(`/api/v1/orth/threads/${accountId}/${propertyId}`, {
        method: 'DELETE',
      });
    },
  });
}

export const useGetIP = () => {
  return useQuery({
    queryKey: ['getIP'],
    queryFn: async () => {
      return await apiFetch<IPLocation>(`/api/v1/ip-location`, {
        method: 'GET',
      });
    },
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

export function useGetFieldDetails(fieldId?: number) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['FIELD_DETAILS', fieldId],
    queryFn: async () => {
      const res = await apiFetch<IFieldDetails>(
        `/api/v1/property-fields/${fieldId}/details`,
        {
          method: 'GET',
        }
      );
      updatePlotsInCache(queryClient, [res]);
      return res;
    },
    enabled: !!fieldId,
    gcTime: Infinity,
  });
}
// ************************ Pending Invitations **************************
export function useGetPendingInvitations() {
  return useQuery({
    queryKey: ['pending-invitations'],
    queryFn: async () => {
      return await apiFetch<PendingInvitationsRes>(
        `/api/v1/account-invitations/me/pending`,
        {
          method: 'GET',
          params: { page: 1, limit: 50 },
        }
      );
    },
    select: (data) => data.invitations,
  });
}
